import { supabase, session, isLoggedIn } from '@/plugins/supabase'
import { i18n } from '@/plugins/i18n'
import db from '@/plugins/database'
import {
  type Json,
  type Tables,
  type TablesInsert
} from '@/types/database.types'
import type { AiSkillDefinition, DocumentType } from '@/types'
import { parseSkillFile, normalizeSkillType } from '@/lib/ai/skill'
import { normalizePromptIcon } from '@/config/ai/skillIcons'
import {
  conversations,
  currentMessages,
  hasMoreConversations,
  loadingMoreConversations,
  userSkillDefinitions,
  hydrated,
  hydrating
} from './state'
import type { TokenUsage, AiConversationInfo, AiMessageInfo } from './types'
import {
  sumTokenUsage,
  appendNewConversations,
  moveConversationToFront,
  splitMessagesAtId
} from './listUpdates'

// Taille de page pour le chargement progressif (infinite scroll) de
// l'historique des conversations.
const CONVERSATIONS_PAGE_SIZE = 30

const EMPTY_USAGE: TokenUsage = {
  inputTokens: 0,
  outputTokens: 0,
  totalTokens: 0,
  cost: 0
}

function isTokenUsage(value: unknown): value is TokenUsage {
  return (
    typeof value === 'object' &&
    value !== null &&
    'inputTokens' in value &&
    'outputTokens' in value &&
    'totalTokens' in value &&
    typeof value.inputTokens === 'number' &&
    typeof value.outputTokens === 'number' &&
    typeof value.totalTokens === 'number'
  )
}

type AiConversationRow = Tables<'ai_conversations'>

function toConversationInfo(
  c: Pick<
    AiConversationRow,
    'id' | 'created_at' | 'updated_at' | 'title' | 'token_usage'
  >
): AiConversationInfo {
  return {
    ...c,
    token_usage: isTokenUsage(c.token_usage)
      ? c.token_usage
      : { ...EMPTY_USAGE }
  }
}

export async function loadConversations() {
  const { data, error } = await supabase
    .from('ai_conversations')
    .select('id, created_at, updated_at, title, token_usage')
    .order('updated_at', { ascending: false })
    .range(0, CONVERSATIONS_PAGE_SIZE - 1)

  if (error) throw error
  conversations.value = (data ?? []).map(toConversationInfo)
  hasMoreConversations.value = (data?.length ?? 0) === CONVERSATIONS_PAGE_SIZE
}

// Charge la page suivante et l'ajoute à la liste. L'offset suit le nombre
// d'éléments déjà chargés ; un dédoublonnage par id absorbe les décalages
// dus aux mises à jour optimistes (création/réordonnancement en tête).
export async function loadMoreConversations() {
  if (loadingMoreConversations.value || !hasMoreConversations.value) return
  loadingMoreConversations.value = true
  try {
    const from = conversations.value.length
    const { data, error } = await supabase
      .from('ai_conversations')
      .select('id, created_at, updated_at, title, token_usage')
      .order('updated_at', { ascending: false })
      .range(from, from + CONVERSATIONS_PAGE_SIZE - 1)

    if (error) throw error
    conversations.value = appendNewConversations(
      conversations.value,
      (data ?? []).map(toConversationInfo)
    )
    hasMoreConversations.value = (data?.length ?? 0) === CONVERSATIONS_PAGE_SIZE
  } finally {
    loadingMoreConversations.value = false
  }
}

export async function loadMessages(conversationId: string) {
  const { data, error } = await supabase
    .from('ai_messages')
    .select('id, created_at, conversation_id, role, content, model, parts')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })

  if (error) throw error
  currentMessages.value = data ?? []
}

export async function createConversation(
  title = i18n.global.t('ai.new-conversation'),
  source: 'text' | 'voice' = 'text'
) {
  const userId = session.value?.user.id
  if (!userId) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('ai_conversations')
    .insert({ title, user_id: userId, source })
    .select('id, created_at, updated_at, title, token_usage')
    .single()

  if (error) throw error
  const conv = toConversationInfo(data)
  conversations.value = [conv, ...conversations.value]
  return conv.id
}

export function addTokenUsage(conversationId: string, usage: TokenUsage) {
  const conv = conversations.value.find(c => c.id === conversationId)
  if (!conv) return

  conv.token_usage = sumTokenUsage(conv.token_usage, usage)

  supabase
    .from('ai_conversations')
    .update({ token_usage: conv.token_usage })
    .eq('id', conversationId)
    .then(({ error }) => {
      if (error) console.error('Failed to persist token usage:', error.message)
      return undefined
    })
}

export async function addMessage(
  conversationId: string,
  role: 'user' | 'assistant',
  content: string,
  model?: string,
  parts?: unknown
) {
  const { error } = await supabase.from('ai_messages').insert({
    conversation_id: conversationId,
    role,
    content,
    model: model ?? null,
    parts: (parts ?? null) as Json
  })

  if (error) throw error

  const now = new Date().toISOString()
  await supabase
    .from('ai_conversations')
    .update({ updated_at: now })
    .eq('id', conversationId)

  conversations.value = moveConversationToFront(
    conversations.value,
    conversationId,
    now
  )
}

export async function updateConversationTitle(id: string, title: string) {
  const { error } = await supabase
    .from('ai_conversations')
    .update({ title })
    .eq('id', id)

  if (error) throw error

  const idx = conversations.value.findIndex(c => c.id === id)
  if (idx !== -1) {
    conversations.value[idx] = { ...conversations.value[idx], title }
  }
}

export async function deleteConversation(id: string) {
  const { error } = await supabase
    .from('ai_conversations')
    .delete()
    .eq('id', id)

  if (error) throw error
  conversations.value = conversations.value.filter(c => c.id !== id)
}

export async function replaceMessages(
  conversationId: string,
  content: string,
  model?: string
): Promise<AiMessageInfo> {
  const { error: deleteError } = await supabase
    .from('ai_messages')
    .delete()
    .eq('conversation_id', conversationId)

  if (deleteError) throw deleteError

  const { data, error } = await supabase
    .from('ai_messages')
    .insert({
      conversation_id: conversationId,
      role: 'assistant',
      content,
      model: model ?? null
    })
    .select('id, created_at, conversation_id, role, content, model, parts')
    .single()

  if (error) throw error

  currentMessages.value = [data]
  return data
}

export async function deleteMessagesAfter(messageId: string): Promise<void> {
  const { kept, removed } = splitMessagesAtId(currentMessages.value, messageId)
  if (removed.length === 0) return

  const { error } = await supabase
    .from('ai_messages')
    .delete()
    .in(
      'id',
      removed.map(m => m.id)
    )

  if (error) throw error

  currentMessages.value = kept
}

export function clearCurrentMessages() {
  currentMessages.value = []
}

// Mappe une ligne Supabase (snake_case) vers une définition de skill (camelCase).
function rowToDefinition(row: Tables<'ai_skills'>): AiSkillDefinition {
  return {
    id: row.id,
    ...(row.labels ? { labels: row.labels as Record<string, string> } : {}),
    icon: normalizePromptIcon(row.icon),
    type: normalizeSkillType(row.type),
    ...(row.order != null ? { order: row.order } : {}),
    ...(row.document_types
      ? { documentTypes: row.document_types as DocumentType[] }
      : {}),
    content: row.content,
    updatedAt: row.updated_at
  }
}

// Mappe une définition de skill vers le payload d'insert/upsert Supabase.
function definitionToRow(
  skill: AiSkillDefinition,
  userId: string
): TablesInsert<'ai_skills'> {
  return {
    id: skill.id,
    user_id: userId,
    labels: skill.labels ?? null,
    icon: normalizePromptIcon(skill.icon),
    type: skill.type,
    document_types: skill.documentTypes ?? null,
    order: skill.order ?? null,
    content: skill.content,
    updated_at: skill.updatedAt ?? new Date().toISOString()
  }
}

// Supabase est la source de vérité des skills utilisateur (l'IA est
// online-only). Migration unique : si des skills subsistent dans l'ancien
// cache Dexie, on les pousse vers Supabase puis on vide la table locale.
export async function migrateLegacyDexieSkills(userId: string) {
  const legacy = await db.aiSkills.toArray()
  if (!legacy.length) return
  const rows = legacy.map(skill =>
    definitionToRow({ ...skill, type: normalizeSkillType(skill.type) }, userId)
  )
  const { error } = await supabase.from('ai_skills').upsert(rows)
  if (error) {
    console.error('ai-skills: échec de la migration Dexie -> Supabase', error)
    return
  }
  try {
    await db.aiSkills.clear()
  } catch (e) {
    // Échec du nettoyage local (IndexedDB bloquée, quota…) : la migration
    // sera re-tentée au prochain login, l'upsert Supabase reste idempotent.
    console.error('ai-skills: échec du nettoyage du cache Dexie', e)
  }
}

export async function loadFromSupabase() {
  const userId = session.value?.user.id
  if (!isLoggedIn.value || !userId) {
    userSkillDefinitions.value = []
    return
  }
  await migrateLegacyDexieSkills(userId)
  const { data, error } = await supabase.from('ai_skills').select('*')
  if (error) {
    console.error('ai-skills: échec du chargement Supabase', error)
    return
  }
  userSkillDefinitions.value = data.map(rowToDefinition)
}

let hydrationPromise: Promise<void> | null = null

export async function hydrate(force = false) {
  if (hydrated.value && !force) return
  // Un rechargement forcé (changement de session) relance un chargement même
  // si un précédent est en cours ; sinon on réutilise la promesse courante.
  if (hydrationPromise && !force) return hydrationPromise

  hydrating.value = true
  hydrationPromise = loadFromSupabase().finally(() => {
    hydrated.value = true
    hydrating.value = false
    hydrationPromise = null
  })

  return hydrationPromise
}

// Mutations optimistes : on met à jour le state local puis on persiste vers
// Supabase ; en cas d'échec on relit le serveur pour resynchroniser et on
// propage l'erreur au caller (formulaire d'édition).
export async function upsertUserSkill(skill: AiSkillDefinition) {
  await hydrate()
  const userId = session.value?.user.id
  if (!isLoggedIn.value || !userId)
    throw new Error('Vous devez être connecté pour enregistrer une compétence')

  // `labels` est la seule source de noms persistée (la colonne `label` a été
  // supprimée). On replie un éventuel `label` hérité (ex. .md importé avec
  // `label:` seul) dans `labels` pour ne pas perdre le nom au rechargement.
  const labels =
    skill.labels ??
    (skill.label ? { [i18n.global.locale.value]: skill.label } : undefined)

  const normalizedSkill: AiSkillDefinition = {
    ...skill,
    label: undefined,
    ...(labels ? { labels } : {}),
    icon: normalizePromptIcon(skill.icon),
    updatedAt: new Date().toISOString()
  }

  const previous = userSkillDefinitions.value
  const exists = previous.some(item => item.id === normalizedSkill.id)
  userSkillDefinitions.value = exists
    ? previous.map(item =>
        item.id === normalizedSkill.id ? normalizedSkill : item
      )
    : [...previous, normalizedSkill]

  const { error } = await supabase
    .from('ai_skills')
    .upsert(definitionToRow(normalizedSkill, userId))
  if (error) {
    userSkillDefinitions.value = previous
    throw new Error(error.message)
  }
}

export async function importSkillFile(file: File): Promise<void> {
  const raw = await file.text()
  const skill = parseSkillFile(raw)
  await upsertUserSkill(skill)
}

export async function removeUserSkill(id: string) {
  await hydrate()
  const userId = session.value?.user.id
  const previous = userSkillDefinitions.value
  userSkillDefinitions.value = previous.filter(skill => skill.id !== id)
  if (!isLoggedIn.value || !userId) return
  // Filtre explicite sur user_id : la PK est composite (user_id, id).
  const { error } = await supabase
    .from('ai_skills')
    .delete()
    .eq('user_id', userId)
    .eq('id', id)
  if (error) {
    userSkillDefinitions.value = previous
    throw new Error(error.message)
  }
}

// Réordonnancement par glisser-déposer : `orderedIds` est la nouvelle liste
// ordonnée des skills utilisateur. On réindexe `order` par pas de 10 et on
// persiste en lot.
export async function reorderUserSkills(orderedIds: string[]) {
  await hydrate()
  const userId = session.value?.user.id
  if (!isLoggedIn.value || !userId) return

  const byId = new Map(userSkillDefinitions.value.map(s => [s.id, s]))
  const reordered: AiSkillDefinition[] = []
  orderedIds.forEach((id, index) => {
    const skill = byId.get(id)
    if (skill) reordered.push({ ...skill, order: (index + 1) * 10 })
  })

  const previous = userSkillDefinitions.value
  userSkillDefinitions.value = reordered

  const { error } = await supabase
    .from('ai_skills')
    .upsert(reordered.map(skill => definitionToRow(skill, userId)))
  if (error) {
    userSkillDefinitions.value = previous
    console.error('ai-skills: échec du réordonnancement Supabase', error)
  }
}
